/*----------------------------------
- HOME: DEMO SCREENS
----------------------------------*/

/**
 * The sample screenshots of Home: tabs 2 to 5 of the demo window (`DemoWindow`) and the closing card's dashboard per
 * persona (`ClosingCard`), with the files and alt texts docs/mockups/website-home-v3.html draws in each panel. Tab 1
 * (Find a name) is the page's real table, never a screenshot.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule No screenshot loads before the visitor opens its tab: these are plain URLs, mounted by `mountedScreens`.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { HomePersona } from '@uniquedomains/components/marketing/demo-window/PersonaSwitch';
import type { DemoScreen, DemoScreens } from '@uniquedomains/components/marketing/demo-window/ScreenPicture';
import AnalysisPhone from '@/client/assets/demo/analysis-m.webp';
import AnalysisDesktop from '@/client/assets/demo/analysis.webp';
import AuctionsPhone from '@/client/assets/demo/auctions-m.webp';
import AuctionsDesktop from '@/client/assets/demo/auctions.webp';
import DashFounderDesktop from '@/client/assets/demo/dash-founder.webp';
import DashInvestorPhone from '@/client/assets/demo/dash-investor-m.webp';
import DashInvestorDesktop from '@/client/assets/demo/dash-investor.webp';
import FinalistsPhone from '@/client/assets/demo/finalists-m.webp';
import FinalistsDesktop from '@/client/assets/demo/finalists.webp';
import FounderHomePhone from '@/client/assets/demo/founder-home-m.webp';
import FounderHomeDesktop from '@/client/assets/demo/founder-home.webp';
import IdeasPhone from '@/client/assets/demo/ideas-m.webp';
import IdeasDesktop from '@/client/assets/demo/ideas.webp';
import RadarPhone from '@/client/assets/demo/radar-m.webp';
import RadarDesktop from '@/client/assets/demo/radar.webp';
import TrendingPhone from '@/client/assets/demo/trending-m.webp';
import TrendingDesktop from '@/client/assets/demo/trending.webp';

/*----------------------------------
- TYPES
----------------------------------*/

/** The ids of the demo window's sample tabs, as the mock names them (`t-<id>` / `p-<id>`); `find` is the live tab. */
export type HomeDemoSampleTabId = 'check' | 'watch' | 'trend' | 'market';

/*----------------------------------
- CONSTANTS
----------------------------------*/

// The files are the mock's own WebP, copied as they are: the desktop cuts are 1920x1080 (the dashboards 2400x1500)
// for a slot 1278px wide at 1440 (1118px for the dashboard), and the phone cuts 780x1320 (analysis 780x1440) for a
// slot 344px wide at 390. None is heavier than its slot needs at 2x (the heaviest, the investor dashboard, is 152 KB),
// so nothing was re-encoded. `DemoScreen` carries no width or height: each picture fills an absolutely placed panel,
// so the browser reserves no box from the file.
// The alt texts are the mock's, except "the brief" on the founder screens, which reads "the search" (website copy rule:
// never "brief" as a noun).

/** Tabs 2 to 5. A tab without `founder` shows one screen to both personas (the mock's Check before you buy). */
export const HOME_DEMO_SCREENS: Record<HomeDemoSampleTabId, DemoScreens> = {
    check: {
        investor: {
            desktop: AnalysisDesktop,
            phone: AnalysisPhone,
            alt: 'The full analysis of harbor.io: price, renewal, five-year cost, est. value, searches, history, registrar prices and a written assessment',
        },
    },
    watch: {
        investor: { desktop: RadarDesktop, phone: RadarPhone, alt: 'A radar page: new names this morning, auctions closing today, names dropping this week' },
        founder: {
            desktop: FounderHomeDesktop,
            phone: FounderHomePhone,
            alt: "A project's Home: the search, the steps from matched to name locked, and today's suggestions to keep or pass",
        },
    },
    trend: {
        investor: {
            desktop: TrendingDesktop,
            phone: TrendingPhone,
            alt: "Trending Domains: today's edition, the names on a clock and what changed since yesterday",
        },
        founder: { desktop: IdeasDesktop, phone: IdeasPhone, alt: 'Business Ideas: rising topics with the open name and a business idea for each' },
    },
    market: {
        investor: {
            desktop: AuctionsDesktop,
            phone: AuctionsPhone,
            alt: 'Auctions: the names closing today by deadline, the bid against est. value, the venue, and the names on your radar marked.',
        },
        founder: {
            desktop: FinalistsDesktop,
            phone: FinalistsPhone,
            alt: "Your finalists side by side: first year, renewal and five-year cost, the lowest marked, with the team's notes",
        },
    },
};

// The mock has no phone cut of the founder's dashboard: its phone source is `founder-home-m.webp`, the phone cut of the
// same project Home the Never miss a name tab shows, so the founder's dashboard reuses it.

/** The closing card's two dashboards, both under its slider since Home v3 (2026-11-04): lazy, far below the fold. */
export const HOME_DASHBOARD_SCREENS: Record<HomePersona, DemoScreen> = {
    investor: {
        desktop: DashInvestorDesktop,
        phone: DashInvestorPhone,
        alt: "An investor's Home: the morning's matches to decide, auctions closing today, drops this week and the best name of the morning",
    },
    founder: {
        desktop: DashFounderDesktop,
        phone: FounderHomePhone,
        alt: "A founder's project Home: the search, the steps from matched to name locked, and today's suggestions to keep or pass",
    },
};

/*----------------------------------
- EXPORTS
----------------------------------*/

export default HOME_DEMO_SCREENS;
